import { supabase } from "../lib/supabase";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export interface Queue {
  name: string;
  waiting: number;
  active: number;
  completed: number;
  failed: number;
  delayed: number;
  total: number;
}

export interface QueueJob {
  id: string;
  name: string;
  data: any;
  opts: {
    attempts?: number;
    delay?: number;
    removeOnComplete?: boolean | { count: number };
    removeOnFail?: boolean | { count: number };
  };
  progress?: number | null;
  attemptsMade: number;
  failedReason?: string;
  timestamp: number;
  processedOn?: number;
  finishedOn?: number;
}

export interface QueueStats {
  waiting: number;
  active: number;
  completed: number;
  failed: number;
  delayed: number;
  paused: boolean;
  total: number;
}

export interface QueueHealth {
  status: "healthy" | "warning" | "critical";
  hasFailures: boolean;
  hasActiveJobs: boolean;
  isPaused: boolean;
}

export interface RecentFailure {
  id: string;
  failedReason: string;
  attemptsMade: number;
  timestamp: number;
}

export interface QueueListResponse {
  success: boolean;
  queues: Queue[];
  total: number;
}

export interface QueueJobsResponse {
  success: boolean;
  queueName: string;
  status: string;
  jobs: QueueJob[];
  pagination: {
    start: number;
    limit: number;
    total: number;
  };
  counts: {
    waiting: number;
    active: number;
    completed: number;
    failed: number;
    delayed: number;
  };
}

export interface QueueStatsResponse {
  success: boolean;
  queueName: string;
  stats: QueueStats;
  health: QueueHealth;
  recentFailures: RecentFailure[];
}

export interface ApiResponse {
  success: boolean;
  message?: string;
}

class QueueMonitoringService {
  private async getAuthHeaders(): Promise<HeadersInit> {
    const {
      data: { session },
      error: sessionError,
    } = await supabase.auth.getSession();

    if (sessionError) {
      throw new Error("Failed to get session information");
    }

    if (!session) {
      throw new Error("You must be logged in to access queue monitoring");
    }

    return {
      Authorization: `Bearer ${session.access_token}`,
      "Content-Type": "application/json",
    };
  }

  async getQueues(): Promise<QueueListResponse> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/api/internal/queues/list`, {
      method: "GET",
      headers,
    });

    if (!response.ok) {
      let errorMessage = `Failed to fetch queues: ${response.statusText}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch (e) {
        // If response is not JSON, try to get text
        try {
          const text = await response.text();
          if (text) {
            errorMessage = text;
          }
        } catch (textError) {
          // Use default error message
        }
      }
      
      // Include status code in error for debugging
      if (response.status === 500) {
        errorMessage = `Internal server error: ${errorMessage}. Please check backend logs.`;
      } else if (response.status === 403) {
        errorMessage = `Permission denied: ${errorMessage}. You may need the 'admin:queues' permission.`;
      } else if (response.status === 401) {
        errorMessage = `Authentication failed: ${errorMessage}. Please log in again.`;
      }
      
      throw new Error(errorMessage);
    }

    return response.json();
  }

  async getJobs(
    queueName: string,
    status: string = "waiting",
    start: number = 0,
    limit: number = 50
  ): Promise<QueueJobsResponse> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(
      `${API_BASE_URL}/api/internal/queues/${encodeURIComponent(
        queueName
      )}/jobs?status=${status}&start=${start}&limit=${limit}`,
      {
        method: "GET",
        headers,
      }
    );

    if (!response.ok) {
      let errorMessage = `Failed to fetch jobs: ${response.statusText}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch (e) {
        try {
          const text = await response.text();
          if (text) errorMessage = text;
        } catch (textError) {
          // Use default error message
        }
      }
      
      if (response.status === 500) {
        errorMessage = `Internal server error: ${errorMessage}. Please check backend logs.`;
      } else if (response.status === 403) {
        errorMessage = `Permission denied: ${errorMessage}. You may need the 'admin:queues' permission.`;
      } else if (response.status === 401) {
        errorMessage = `Authentication failed: ${errorMessage}. Please log in again.`;
      }
      
      throw new Error(errorMessage);
    }

    return response.json();
  }

  async getQueueStats(queueName: string): Promise<QueueStatsResponse> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(
      `${API_BASE_URL}/api/internal/queues/${encodeURIComponent(
        queueName
      )}/stats`,
      {
        method: "GET",
        headers,
      }
    );

    if (!response.ok) {
      let errorMessage = `Failed to fetch queue stats: ${response.statusText}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch (e) {
        try {
          const text = await response.text();
          if (text) errorMessage = text;
        } catch (textError) {
          // Use default error message
        }
      }
      
      if (response.status === 500) {
        errorMessage = `Internal server error: ${errorMessage}. Please check backend logs.`;
      } else if (response.status === 403) {
        errorMessage = `Permission denied: ${errorMessage}. You may need the 'admin:queues' permission.`;
      } else if (response.status === 401) {
        errorMessage = `Authentication failed: ${errorMessage}. Please log in again.`;
      }
      
      throw new Error(errorMessage);
    }

    return response.json();
  }

  async retryJob(queueName: string, jobId: string): Promise<ApiResponse> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(
      `${API_BASE_URL}/api/internal/queues/${encodeURIComponent(
        queueName
      )}/jobs/${encodeURIComponent(jobId)}/retry`,
      {
        method: "POST",
        headers,
      }
    );

    if (!response.ok) {
      let errorMessage = `Failed to retry job: ${response.statusText}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch (e) {
        try {
          const text = await response.text();
          if (text) errorMessage = text;
        } catch (textError) {
          // Use default error message
        }
      }
      
      if (response.status === 500) {
        errorMessage = `Internal server error: ${errorMessage}. Please check backend logs.`;
      }
      
      throw new Error(errorMessage);
    }

    return response.json();
  }

  async removeJob(queueName: string, jobId: string): Promise<ApiResponse> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(
      `${API_BASE_URL}/api/internal/queues/${encodeURIComponent(
        queueName
      )}/jobs/${encodeURIComponent(jobId)}`,
      {
        method: "DELETE",
        headers,
      }
    );

    if (!response.ok) {
      let errorMessage = `Failed to remove job: ${response.statusText}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch (e) {
        try {
          const text = await response.text();
          if (text) errorMessage = text;
        } catch (textError) {
          // Use default error message
        }
      }
      
      if (response.status === 500) {
        errorMessage = `Internal server error: ${errorMessage}. Please check backend logs.`;
      }
      
      throw new Error(errorMessage);
    }

    return response.json();
  }
}

export const queueMonitoringService = new QueueMonitoringService();
